import { Component, computed, inject, input, signal } from "@angular/core";
import {
  JobRef,
  TracefitData,
  TracefitPlotData,
  TracefitShot,
} from "../models/trace-fit-response-model";
import { NgxEchartsDirective, NgxEchartsModule } from "ngx-echarts";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { NzSelectModule } from "ng-zorro-antd/select";
import { NzRadioModule } from "ng-zorro-antd/radio";
import { NzDividerModule } from "ng-zorro-antd/divider";
import { NzIconModule } from "ng-zorro-antd/icon";
import { NzPopoverModule } from "ng-zorro-antd/popover";
import { rxResource } from "@angular/core/rxjs-interop";
import { forkJoin, map, switchMap } from "rxjs";
import { ProjectService } from "../../shared/services/project.service";

/** Raw shape returned by ProjectService.getTraceFitPlottingData. */
interface TraceFitPlottingResponse {
  metrics: Record<string, string>;
  /** Keyed by job id (confirmed against the legacy trace-fits-plotting page, which compares these keys to job.id). */
  tracedata: Record<
    string,
    {
      job_data: Record<string, any>;
      iterations_data: Record<string, Record<string, number>>;
      spatial_data: Record<
        string,
        Record<string, Record<string, string | number>>
      >;
    }
  >;
}

/**
 * Reshapes the tracefits-plotting endpoint's response — metric-first
 * iteration data, generic x/y/z shot coordinates — into the iteration-first
 * {@link TracefitData} shape this component's charts expect.
 * inline/xline are derived from x/y using the job's own x2_is_inline flag
 * (from the input payload), matching the mapping used in the legacy
 * trace-fits-plotting page: x2_is_inline swaps which raw axis is inline.
 */
function toTracefitDataMap(
  res: TraceFitPlottingResponse,
  jobsById: Map<string, JobRef>,
): Record<string, TracefitData> {
  const result: Record<string, TracefitData> = {};

  for (const [jobId, entry] of Object.entries(res.tracedata)) {
    const x2IsInline = !!jobsById.get(jobId)?.x2_is_inline;

    const average: Record<string, Record<string, number>> = {};
    for (const [metric, byIteration] of Object.entries(
      entry.iterations_data ?? {},
    )) {
      for (const [iter, value] of Object.entries(byIteration)) {
        (average[iter] ??= {})[metric] = Number(value);
      }
    }

    const spatial: Record<string, Record<string, TracefitShot>> = {};
    for (const [iter, shots] of Object.entries(entry.spatial_data ?? {})) {
      const byShot: Record<string, TracefitShot> = {};
      for (const [shotId, shot] of Object.entries(shots)) {
        const { shot_id, x, y, z, ...metrics } = shot;
        const numericMetrics: Record<string, number | null> = {};
        for (const [key, value] of Object.entries(metrics)) {
          const n = Number(value);
          numericMetrics[key] = Number.isFinite(n) ? n : null;
        }
        byShot[shotId] = {
          shot_id,
          ...numericMetrics,
          coordinates: {
            inline: Number(x2IsInline ? y : x),
            xline: Number(x2IsInline ? x : y),
            depth: Number(z),
          },
        } as TracefitShot;
      }
      spatial[iter] = byShot;
    }

    result[jobId] = { average, spatial };
  }

  return result;
}

/**
 * Renders the structured output of the Trace-fit specialist, found in
 * `detail.data[]`. The payload shape is still being defined — for now the
 * component just receives its data and shows the raw payload so nothing is
 * silently dropped. We'll build out the real rendering next.
 */
@Component({
  selector: "app-xwai-trace-fit",
  templateUrl: "./xwai-trace-fit.component.html",
  styleUrls: ["./xwai-trace-fit.component.less"],
  imports: [
    NgxEchartsDirective,
    CommonModule,
    FormsModule,
    NzSelectModule,
    NzRadioModule,
    NzDividerModule,
    NzIconModule,
    NzPopoverModule,
  ],
})
export class XwaiTraceFitComponent {
  private readonly projectService = inject(ProjectService);

  readonly data = input.required<TracefitPlotData>();

  protected jobs = computed(() => this.data().jobs);

  /**
   * Fetches trace-fit data for the current jobs. Each job's project id isn't
   * in the payload, so it's resolved per job via getJobDetail first; jobs are
   * then grouped by project (getTraceFitPlottingData is project-scoped) and
   * the results merged into a single job-id-keyed map.
   */
  protected readonly tracefits = rxResource<
    Record<string, TracefitData>,
    JobRef[] | undefined
  >({
    params: () => {
      const jobs = this.jobs();
      return jobs.length ? jobs : undefined;
    },
    stream: ({ params: jobs }) => {
      const jobsById = new Map(jobs.map((j) => [j.id, j]));
      return forkJoin(
        jobs.map((job) =>
          this.projectService
            .getJobDetail(job.id)
            .pipe(map((detail) => ({ jobId: job.id, projectId: detail.projectId }))),
        ),
      ).pipe(
        switchMap((jobProjects) => {
          const jobIdsByProject = new Map<string, string[]>();
          for (const { jobId, projectId } of jobProjects) {
            const list = jobIdsByProject.get(projectId) ?? [];
            list.push(jobId);
            jobIdsByProject.set(projectId, list);
          }
          return forkJoin(
            Array.from(jobIdsByProject.entries()).map(([projectId, jobIds]) =>
              this.projectService
                .getTraceFitPlottingData(projectId, {
                  job_ids: jobIds,
                  parameters: [],
                })
                .pipe(map((res) => toTracefitDataMap(res, jobsById))),
            ),
          );
        }),
        map((maps) => Object.assign({}, ...maps)),
      );
    },
  });

  /** Metric/profile keys available across all jobs' iterations, for the selector. */
  protected metrics = computed(() => {
    const fits = this.tracefits.value();
    if (!fits) return [];
    const keys = new Set<string>();
    for (const { id } of this.jobs()) {
      const avg = fits[id]?.average;
      if (!avg) continue;
      for (const iter of Object.keys(avg)) {
        Object.keys(avg[iter] ?? {}).forEach((k) => keys.add(k));
      }
    }
    return Array.from(keys);
  });

  /** Metric options for the Profile select, with prettified labels. Memoized. */
  protected metricOptions = computed(() =>
    this.metrics().map((value) => ({ value, label: this.prettyLabel(value) })),
  );

  protected traceType = signal<"iteration" | "spatial">("spatial");

  /** Render series as connected lines or discrete points. */
  protected plotType = signal<"line" | "scatter">("line");

  /** Spatial view: which positional axis to plot along. */
  protected spatialAxis = signal<"inline" | "xline">("inline");
  /** Spatial view: aggregation window size, in coordinate units (~metres). */
  protected windowSize = signal<number>(50);
  /** Spatial view: which iteration's per-shot data to show (null → latest). */
  protected selectedSpatialIteration = signal<string | null>(null);

  // Axis scale limits (null → auto). X limits apply to the spatial value axis only.
  protected xMin = signal<number | null>(null);
  protected xMax = signal<number | null>(null);
  protected yMin = signal<number | null>(null);
  protected yMax = signal<number | null>(null);

  /** Parse a numeric input into a limit, treating blanks/invalid as auto (null). */
  protected toLimit(value: unknown): number | null {
    if (value === "" || value == null) return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  }

  /** Iterations that carry spatial (per-shot) data, across all jobs. */
  protected spatialIterations = computed(() => {
    const fits = this.tracefits.value();
    if (!fits) return [];
    const iters = new Set<string>();
    for (const { id } of this.jobs()) {
      const sp = fits[id]?.spatial;
      if (sp) Object.keys(sp).forEach((i) => iters.add(i));
    }
    return Array.from(iters).sort((a, b) => Number(a) - Number(b));
  });

  /** The spatial iteration actually shown: the selection, falling back to the
   *  lowest of the jobs' latest iterations, so every job has data by default. */
  protected resolvedIteration = computed(() => {
    const sel = this.selectedSpatialIteration();
    const iters = this.spatialIterations();
    if (sel && iters.includes(sel)) return sel;
    const fits = this.tracefits.value();
    const jobMaxes = this.jobs()
      .map(({ id }) => Object.keys(fits?.[id]?.spatial ?? {}).map(Number))
      .filter((keys) => keys.length)
      .map((keys) => Math.max(...keys));
    if (!jobMaxes.length) return null;
    const lowest = Math.min(...jobMaxes);
    return iters.find((i) => Number(i) === lowest) ?? null;
  });

  protected selectedFit = signal<string>("traces_fit");
  // make the echart options
  protected chartOptions = computed(() => {
    const fits = this.tracefits.value();
    const metric = this.selectedFit();
    const plotType = this.plotType();
    const xMin = this.xMin() ?? undefined;
    const xMax = this.xMax() ?? undefined;

    if (!fits) {
      return { series: [] };
    }

    if (this.traceType() == "iteration") {
      const allIterations = new Set<string>();
      this.jobs().forEach(({ id }) => {
        Object.keys(fits[id]?.average ?? {}).forEach((iter) =>
          allIterations.add(iter),
        );
      });
      const sortedIterations = Array.from(allIterations).sort(
        (a, b) => Number(a) - Number(b),
      );

      const seriesData = this.jobs().map(({ id, name }) => {
        const jobTimeline = fits[id]?.average ?? {};

        const dataPoints = sortedIterations.map((iter) => {
          // If a specific job doesn't have data for this iteration,
          // return null so ECharts skips or breaks the line gracefully
          return jobTimeline[iter] ? jobTimeline[iter][metric] : null;
        });

        return {
          name: name, // This name pairs directly with the legend
          type: plotType,
          data: dataPoints,
          smooth: plotType === "line",
          symbolSize: 6,
          lineStyle: { width: 2 },
        };
      });
      return {
        tooltip: {
          trigger: "axis",
          confine: true, // Keeps the tooltip box from clipping outside the chart edges
          valueFormatter: (value: any) => this.formatValue(value),
        },
        legend: {
          data: this.jobs(),
          // top: '12%',
          type: "scroll", // Adds navigation controls automatically if you have dozens of jobs
        },
        grid: {
          left: "4%",
          right: "4%",
          bottom: "10%",
          // top: '10%', // Lowered slightly to make clean room for the multi-job legend
          containLabel: true,
        },
        xAxis: {
          type: "category",
          name: "Iteration",
          nameLocation: "middle",
          nameGap: 30,
          data: sortedIterations,
        },
        yAxis: {
          type: "value",
          name: "Scale Value",
          scale: true, // Keeps bounds tight to focus on subtle value drift
          ...this.resolveYBounds(
            seriesData.flatMap((s) => s.data),
          ),
        },
        series: seriesData,
      };
    } else {
      // Spatial view: plot the chosen metric along inline/crossline, aggregating
      // shots into fixed-width windows so dense, irregularly-spaced shots read cleanly.
      const axis = this.spatialAxis();
      const iter = this.resolvedIteration();
      const window = this.windowSize() > 0 ? this.windowSize() : 50;

      const seriesData = this.jobs().map(({ id, name }) => {
        const iterData =
          iter != null ? fits[id]?.spatial?.[iter] : undefined;

        // Bin shots by position; accumulate sum+count to average per window.
        const bins = new Map<number, { sum: number; count: number }>();
        if (iterData) {
          for (const shotId of Object.keys(iterData)) {
            const shot = iterData[shotId];
            const coords = shot.coordinates;
            const value = shot[metric];
            if (!coords || typeof value !== "number") continue;
            const pos = coords[axis];
            if (typeof pos !== "number") continue;
            const binIdx = Math.floor(pos / window);
            const bin = bins.get(binIdx) ?? { sum: 0, count: 0 };
            bin.sum += value;
            bin.count += 1;
            bins.set(binIdx, bin);
          }
        }

        // Plot the window centre against the windowed mean, ordered along the axis.
        const dataPoints = Array.from(bins.entries())
          .map(([binIdx, bin]) => [
            binIdx * window + window / 2,
            bin.sum / bin.count,
          ])
          .sort((a, b) => a[0] - b[0]);

        return {
          name,
          type: plotType,
          data: dataPoints,
          smooth: plotType === "line",
          symbolSize: 6,
          lineStyle: { width: 2 },
        };
      });

      // Grey out legend entries for jobs with no data at the selected iteration.
      const inactive = "#ccc";
      const legendData = seriesData.map(({ name, data }) =>
        data.length
          ? { name }
          : { name, itemStyle: { color: inactive }, textStyle: { color: inactive } },
      );

      return {
        tooltip: { trigger: "axis", confine: true },
        legend: { type: "scroll", data: legendData },
        grid: { left: "4%", right: "4%", bottom: "10%", containLabel: true },
        xAxis: {
          type: "value",
          name: axis === "inline" ? "Inline" : "Crossline",
          nameLocation: "middle",
          nameGap: 30,
          scale: true,
          min: xMin,
          max: xMax,
        },
        yAxis: {
          type: "value",
          name: "Scale Value",
          scale: true,
          ...this.resolveYBounds(seriesData.flatMap((s) => s.data.map((p) => p[1]))),
        },
        series: seriesData,
      };
    }
  });

  /**
   * Resolve the y-axis min/max. A user-entered limit always wins; otherwise
   * default to a fixed 0–100 scale when every plotted value falls in that range
   * (e.g. percentage-style fits), and auto-scale otherwise.
   */
  private resolveYBounds(values: Array<number | null>): {
    min?: number;
    max?: number;
  } {
    const nums = values.filter((v): v is number => typeof v === "number");
    // Pin to 0–100 only for percentage-like data: all values in range AND a
    // large enough spread (max ≥ 10). Small decimals auto-scale so detail shows.
    const useUnitScale =
      nums.length > 0 &&
      nums.every((v) => v >= 0 && v <= 100) &&
      Math.max(...nums) >= 10;
    return {
      min: this.yMin() ?? (useUnitScale ? 0 : undefined),
      max: this.yMax() ?? (useUnitScale ? 100 : undefined),
    };
  }

  /** Format a metric value to 3 significant figures, trimming trailing zeros. */
  protected formatValue(value: unknown): string {
    if (typeof value !== "number" || Number.isNaN(value)) return "—";
    return String(parseFloat(value.toPrecision(3)));
  }

  /** Turn a snake_case metric key into a nicer label, e.g. "traces_fit" → "Traces Fit". */
  protected prettyLabel(key: string): string {
    return key
      .split("_")
      .map((word) => (word ? word.charAt(0).toUpperCase() + word.slice(1) : word))
      .join(" ");
  }

}
